import { APP_CONFIG } from 'src/config.app'
import { ADAxios, ApiActionNames } from 'src/services/AxiosInterceptor'

export type ModuleAIReviewStatus =
    | 'NOT_REQUESTED'
    | 'PENDING'
    | 'IN_PROGRESS'
    | 'COMPLETED'
    | 'FAILED'

export type ModuleAIReviewSeverity = 'HIGH' | 'MEDIUM' | 'LOW'

export interface ModuleAIReviewFinding {
    findingId: string
    ruleId: string
    severity: ModuleAIReviewSeverity
    message: string
    entityId?: string
    attributePath?: string
    locale?: string
}

export interface ModuleAIReviewSeverityCounts {
    high?: number
    medium?: number
    low?: number
}

export interface ModuleAIReviewSection {
    sectionId: string
    title?: string
    status: 'FINDINGS' | 'PASS' | 'NOT_APPLICABLE'
    rulesEvaluated?: number
    counts?: ModuleAIReviewSeverityCounts
    findings?: ModuleAIReviewFinding[]
}

export interface ModuleAIReviewSummary extends ModuleAIReviewSeverityCounts {
    total?: number
    itemsChecked?: number
    sectionsWithFindings?: number
}

export interface ModuleAIReview {
    status: ModuleAIReviewStatus
    moduleVersionId?: string
    reviewId?: string
    revisionNumber?: string
    summary?: ModuleAIReviewSummary
    sections?: ModuleAIReviewSection[]
    promptVersion?: string
    rubricVersion?: string
    modelId?: string
    /** Epoch milliseconds. */
    startedAt?: number
    /** Epoch milliseconds. */
    completedAt?: number
    failureReason?: string
}

/**
 * Client for the advisory AI review of a module review revision (GetModuleAIReview on
 * RxtHofAuthoringService). The review is produced asynchronously after a module enters review, so
 * callers poll while it is PENDING or IN_PROGRESS. A revision that was never analysed comes back
 * as NOT_REQUESTED rather than an error.
 */
export class ModuleAIReviewService {
    public static async getModuleAIReview(
        moduleVersionId: string,
        reviewId: string,
        revisionNumber: string,
        abortController?: AbortController
    ): Promise<ModuleAIReview> {
        const { data }: { data: ModuleAIReview } = await ADAxios.get(
            `${APP_CONFIG.backendAPIBaseUrl}/modules-versions/${encodeURIComponent(
                moduleVersionId
            )}/ai-review/${encodeURIComponent(reviewId)}/revisions/${encodeURIComponent(
                revisionNumber
            )}`,
            {
                apiActionName: ApiActionNames.GetModuleAIReview,
                signal: abortController?.signal,
            }
        )
        return data
    }
}
